import { call, delay, fork, put, take } from 'redux-saga/effects';
import {Api} from '../api';
import {DoorStatus} from '../api/types';
import {FetchDoorStatusPayload, fetchDoorStatus} from '../action/depotsCommands';
import {doorStatusFetchFailed, doorStatusFetchStarted, doorStatusFetched} from '../action/depotsEvents';
import {Action} from 'redux-actions';
import {enqueueWarningSnackbar} from '../action/snackbarCommands';
import {getI18n} from 'react-i18next';
import Logger from '../common/logger';

/**
 * Shortest time the reload indicator stays visible, in milliseconds.
 *
 * On a fast connection the request returns in well under 100ms and the
 * indicator would flash — read as a glitch rather than as "fetching". The
 * floor applies to the whole fetch, not just the indicator, so the refreshed
 * lockers appear exactly when the spinning stops; releasing the data earlier
 * would show new values under an indicator still claiming to be busy.
 */
export const MIN_VISIBLE_FETCH_MS = 400;

const holdForMinimumVisibleDuration = function*(startedAt: number) {
    const elapsed = Date.now() - startedAt;

    if (elapsed < MIN_VISIBLE_FETCH_MS) {
        yield delay(MIN_VISIBLE_FETCH_MS - elapsed);
    }
};

export const onFetchDoorStatus = function*(depotId: string) {
    yield put(doorStatusFetchStarted({ depotId }));

    const startedAt = Date.now();

    try {
        const doorStatus: DoorStatus = yield call(Api.getDoorStatus, depotId);

        yield call(holdForMinimumVisibleDuration, startedAt);
        yield put(doorStatusFetched({ depotId, doorStatus }));
    } catch (e) {
        Logger('Saga').error(e);

        yield call(holdForMinimumVisibleDuration, startedAt);
        // Clears the indicator. The snackbar below is what tells the operator
        // the lockers on screen are still the old ones.
        yield put(doorStatusFetchFailed({ depotId }));
        yield put(enqueueWarningSnackbar({ message: getI18n().t('snackbar.warning.fetch_door_status') }));
    }
};

export function* fetchDoorStatusFlow() {
    while (true) {
        const action: Action<FetchDoorStatusPayload> = yield take(fetchDoorStatus.toString());

        yield fork(onFetchDoorStatus, action.payload.depotId);
    }
}
